import { useChainInfo } from '@/state/deployments/hooks'
import { displayFiat } from '@/util/displayFiat'
import { BigNumber } from 'ethers'
import { useCallback } from 'react'
import {
  useConvertNativeCurrencyToFiat,
  useConvertNativeCurrencyToSwEth,
  useConvertNativeCurrencyToRswEth,
} from '../useCurrencyConversions'
import { useToFloatNativeCurrency } from '../useNativeCurrencyFormatting'
import { useDisplaySwEth } from '../useSwEthDisplay'
import { useDisplayRswEth } from '@/hooks/useRswEthDisplay'
import { formatWithConfig } from '@/util/number'

export const useDisplayNativeCurrency = () => {
  const toFloatNativeCurrency = useToFloatNativeCurrency()
  const { nativeCurrency } = useChainInfo()

  return useCallback(
    (
      amountBN: BigNumber,
      {
        precision = 4,
        localize = true,
        numberOnly,
      }: {
        precision?: number
        localize?: boolean
        numberOnly?: boolean
      } = {}
    ) => {
      const floatAmount = toFloatNativeCurrency(amountBN)
      const formattedFloat = formatWithConfig(floatAmount, {
        precision,
        localize,
      })

      if (numberOnly) {
        return `${formattedFloat}`
      }

      return `${formattedFloat} ${nativeCurrency.symbol}`
    },
    [nativeCurrency.symbol, toFloatNativeCurrency]
  )
}

export const useDisplayNativeCurrencyPriceFiat = () => {
  const convertNativeCurrencyToFiat = useConvertNativeCurrencyToFiat()

  return useCallback(
    (
      nativeCurrencyAmountBN: BigNumber,
      nativeToFiatRate: number,
      rounded?: boolean
    ) => {
      return displayFiat(
        convertNativeCurrencyToFiat(nativeCurrencyAmountBN, nativeToFiatRate),
        rounded
      )
    },
    [convertNativeCurrencyToFiat]
  )
}

export const useDisplayNativeCurrencyInSwEth = () => {
  const displaySwEth = useDisplaySwEth()
  const convertNativeCurrencyToSwEth = useConvertNativeCurrencyToSwEth()

  return useCallback(
    (
      ethAmountBN: BigNumber,
      nativeToSwEthRate: number,
      {
        precision,
        localize,
        numberOnly,
      }: { precision?: number; localize?: boolean; numberOnly?: boolean } = {}
    ) => {
      return displaySwEth(
        convertNativeCurrencyToSwEth(ethAmountBN, nativeToSwEthRate),
        { precision, localize, numberOnly }
      )
    },
    [convertNativeCurrencyToSwEth, displaySwEth]
  )
}

export const useDisplayNativeCurrencyInRswEth = () => {
  const displayRswEth = useDisplayRswEth()
  const convertNativeCurrencyToRswEth = useConvertNativeCurrencyToRswEth()

  return useCallback(
    (
      ethAmountBN: BigNumber,
      nativeToRswEthRate: number,
      {
        precision,
        localize,
        numberOnly,
      }: { precision?: number; localize?: boolean; numberOnly?: boolean } = {}
    ) => {
      return displayRswEth(
        convertNativeCurrencyToRswEth(ethAmountBN, nativeToRswEthRate),
        { precision, localize, numberOnly }
      )
    },
    [convertNativeCurrencyToRswEth, displayRswEth]
  )
}
